import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatErr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
    AlertDialogFooter, AlertDialogCancel, AlertDialogAction, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { AdminSecurity } from "@/components/AdminSecurity";
import { SupportConfig } from "@/components/SupportConfig";
import { PlansManager } from "@/components/PlansManager";
import { SchoolManageDialog } from "@/components/SchoolManageDialog";
import { toast } from "sonner";
import { Loader2, Plus, Building2, Users, ScanLine, BadgeCheck, Pencil, Trash2, ShieldCheck, LifeBuoy, CreditCard } from "lucide-react";

export default function SuperAdmin() {
    const [stats, setStats] = useState(null);
    const [tenants, setTenants] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", school_route: "", plan: "standard" });
    const [manage, setManage] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const [s, t] = await Promise.all([api.get("/admin/stats"), api.get("/admin/tenants")]);
        setStats(s.data); setTenants(t.data);
    };
    useEffect(() => { load().catch(() => {}); }, []);

    const create = async () => {
        if (!form.name || !form.school_route) { toast.error("Name and school route required"); return; }
        setBusy(true);
        try {
            await api.post("/admin/tenants", form);
            toast.success("School tenant created");
            setOpen(false); setForm({ name: "", school_route: "", plan: "standard" }); load();
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); } finally { setBusy(false); }
    };

    const setStatus = async (tid, status) => {
        try { await api.post(`/admin/tenants/${tid}/status?status=${status}`); toast.success("Status updated"); load(); }
        catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    };
    const del = async (tid) => {
        try { await api.delete(`/admin/tenants/${tid}`); toast.success("School deleted"); load(); }
        catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    };

    const CARDS = stats ? [
        { label: "School Tenants", value: stats.tenant_count, icon: Building2 },
        { label: "Active Subscriptions", value: stats.active_subscriptions, icon: BadgeCheck },
        { label: "Total Users", value: stats.total_users, icon: Users },
        { label: "Invoices Processed", value: stats.total_invoices, icon: ScanLine },
    ] : [];

    return (
        <div className="space-y-6 pe-fade-up" data-testid="super-admin">
            <div>
                <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-brand-emerald">Platform Console</h1>
                <p className="text-muted-foreground text-sm mt-1">Manage school tenants, subscriptions, admin security and email across ProcureEd.</p>
            </div>

            <Tabs defaultValue="schools">
                <TabsList>
                    <TabsTrigger value="schools" data-testid="tab-schools"><Building2 className="h-4 w-4 mr-1.5" /> Schools</TabsTrigger>
                    <TabsTrigger value="plans" data-testid="tab-plans"><CreditCard className="h-4 w-4 mr-1.5" /> Plans</TabsTrigger>
                    <TabsTrigger value="security" data-testid="tab-security-email"><ShieldCheck className="h-4 w-4 mr-1.5" /> Security & Email</TabsTrigger>
                    <TabsTrigger value="support" data-testid="tab-support"><LifeBuoy className="h-4 w-4 mr-1.5" /> Support</TabsTrigger>
                </TabsList>

                <TabsContent value="schools" className="mt-5 space-y-6">
                    <div className="flex justify-end">
                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild>
                                <Button className="bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" data-testid="add-tenant-button"><Plus className="h-4 w-4 mr-1" /> Add School</Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle>Provision a new school</DialogTitle></DialogHeader>
                                <div className="space-y-4">
                                    <div><Label>School name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Riverside Prep" className="mt-1.5" data-testid="tenant-name-input" /></div>
                                    <div><Label>Veracross school route</Label><Input value={form.school_route} onChange={(e) => setForm({ ...form, school_route: e.target.value })} placeholder="e.g. riverside" className="mt-1.5 font-mono" data-testid="tenant-route-input" /></div>
                                    <div><Label>Plan</Label>
                                        <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                                            <SelectTrigger className="mt-1.5" data-testid="tenant-plan-select"><SelectValue /></SelectTrigger>
                                            <SelectContent><SelectItem value="standard">Standard K-12</SelectItem><SelectItem value="enterprise">Enterprise Academy</SelectItem></SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                                    <Button disabled={busy} onClick={create} className="bg-brand-emerald hover:bg-brand-emeraldhover" data-testid="save-tenant-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>

                    {!stats ? (
                        <div className="grid place-items-center py-16 text-brand-emerald"><Loader2 className="h-6 w-6 animate-spin" /></div>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                {CARDS.map((c) => {
                                    const Icon = c.icon;
                                    return (
                                        <div key={c.label} className="bg-white border border-border rounded-xl p-5" data-testid={`admin-stat-${c.label.split(" ")[0].toLowerCase()}`}>
                                            <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">{c.label}</span><Icon className="h-4 w-4 text-brand-teal" /></div>
                                            <div className="mt-3 text-3xl font-semibold font-display text-brand-emerald">{c.value}</div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="bg-white border border-border rounded-xl overflow-hidden">
                                <div className="px-5 py-4 border-b border-border font-semibold text-brand-emerald">School Tenants</div>
                                <div className="overflow-x-auto pe-scroll">
                                    <table className="w-full text-sm" data-testid="tenant-table">
                                        <thead><tr className="text-left text-muted-foreground border-b border-border bg-secondary/40">
                                            <th className="p-3 font-medium">School</th>
                                            <th className="p-3 font-medium">Route</th>
                                            <th className="p-3 font-medium">Plan</th>
                                            <th className="p-3 font-medium text-right">Users</th>
                                            <th className="p-3 font-medium text-right">Invoices</th>
                                            <th className="p-3 font-medium">Subscription</th>
                                            <th className="p-3 font-medium text-right">Actions</th>
                                        </tr></thead>
                                        <tbody>
                                            {tenants.map((t) => (
                                                <tr key={t.id} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`tenant-row-${t.school_route}`}>
                                                    <td className="p-3 font-medium">{t.name}</td>
                                                    <td className="p-3 font-mono text-xs text-muted-foreground">{t.school_route}</td>
                                                    <td className="p-3 capitalize">{t.plan}</td>
                                                    <td className="p-3 text-right">{t.user_count}</td>
                                                    <td className="p-3 text-right">{t.invoice_count}</td>
                                                    <td className="p-3">
                                                        <Select value={t.subscription_status} onValueChange={(v) => setStatus(t.id, v)}>
                                                            <SelectTrigger className="h-8 w-[130px]" data-testid={`tenant-status-${t.school_route}`}><SelectValue /></SelectTrigger>
                                                            <SelectContent>
                                                                {["active", "trialing", "past_due", "canceled", "suspended"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                                            </SelectContent>
                                                        </Select>
                                                    </td>
                                                    <td className="p-3">
                                                        <div className="flex items-center justify-end gap-1">
                                                            <Button variant="ghost" size="icon" onClick={() => setManage(t)} data-testid={`manage-tenant-${t.school_route}`}><Pencil className="h-4 w-4" /></Button>
                                                            <AlertDialog>
                                                                <AlertDialogTrigger asChild>
                                                                    <Button variant="ghost" size="icon" className="text-rose-600" data-testid={`delete-tenant-${t.school_route}`}><Trash2 className="h-4 w-4" /></Button>
                                                                </AlertDialogTrigger>
                                                                <AlertDialogContent>
                                                                    <AlertDialogHeader>
                                                                        <AlertDialogTitle>Delete {t.name}?</AlertDialogTitle>
                                                                        <AlertDialogDescription>This permanently removes the school and all of its users, requests, POs and invoices. This cannot be undone.</AlertDialogDescription>
                                                                    </AlertDialogHeader>
                                                                    <AlertDialogFooter>
                                                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                                        <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => del(t.id)} data-testid={`confirm-delete-${t.school_route}`}>Delete school</AlertDialogAction>
                                                                    </AlertDialogFooter>
                                                                </AlertDialogContent>
                                                            </AlertDialog>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}
                </TabsContent>

                <TabsContent value="security" className="mt-5">
                    <AdminSecurity />
                </TabsContent>

                <TabsContent value="plans" className="mt-5">
                    <PlansManager />
                </TabsContent>

                <TabsContent value="support" className="mt-5">
                    <SupportConfig />
                </TabsContent>
            </Tabs>

            <SchoolManageDialog tenant={manage} onClose={() => setManage(null)} onChanged={load} />
        </div>
    );
}
